/*
 * Copyright (C) 2019 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { ChangeDetectorRef, Component, Inject, NgZone, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { marker } from '@jsverse/transloco-keys-manager/marker';
import { TranslocoService } from '@ngneat/transloco';
import { Select } from '@ngxs/store';
import { ReCaptchaV3Service, RecaptchaComponent } from 'ng-recaptcha-2';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { ACCOUNT_DATA_SERVICE, AccountDataService } from '../../../page-modules/account/services/account-data.service';
import { RedirectHelper } from '../../../page-modules/resource/store/editor/content/helpers/redirect.helper';
import { SnackbarHelper } from '../../../shared/helpers/snackbar-helper';
import { Organization } from '../../../shared/models';
import { MaintenanceService } from '../../../shared/services/maintenance/maintenance.service';
import { UserAuthState } from '../../store/user-auth.state';

@Component({
  templateUrl: './forgot-password.component.html',
  styleUrls: ['./forgot-password.component.scss'],
})
export class ForgotPasswordComponent implements OnInit {
  @Select(UserAuthState.organizationDetails)
  organizationData$: Observable<Organization>;

  @ViewChild('captchaElem', { static: false }) captchaElem: RecaptchaComponent;

  isResetPasswordExpire: boolean;
  errorText: string;
  forgotPasswordForm: FormGroup;
  passwordRecoveryInProgress: boolean;
  reCaptchaSiteKeyV2 = environment.reCaptchaSiteKeyV2;
  showMaintenanceOverlay: boolean;

  private isPreArrivalPage: boolean;

  constructor(
    private fb: FormBuilder,
    @Inject(ACCOUNT_DATA_SERVICE) private accountService: AccountDataService,
    private router: Router,
    private snackBar: MatSnackBar,
    private ngZone: NgZone,
    private activatedRoute: ActivatedRoute,
    private translocoService: TranslocoService,
    private cdr: ChangeDetectorRef,
    private captchaV3Service: ReCaptchaV3Service,
    private maintenanceService: MaintenanceService,
  ) {
    this.showMaintenanceOverlay = this.maintenanceService.showMaintenanceMessage();
  }

  ngOnInit() {
    this.isPreArrivalPage = document.location.pathname === '/prearrivals/forgot-password';
    this.isResetPasswordExpire = !!this.activatedRoute.snapshot?.queryParamMap?.get('expire');
    this.errorText = this.activatedRoute.snapshot?.queryParamMap?.get('message');

    this.forgotPasswordForm = this.fb.group({
      email: ['', Validators.compose([Validators.required, Validators.email])],
    });
  }

  sendRecoveryRequest() {
    if (this.forgotPasswordForm.valid) {
      this.captchaElem.execute();
    }
  }

  onLoginClick() {
    if (this.isPreArrivalPage) {
      RedirectHelper.redirectByUrl(this.ngZone, this.router, this.activatedRoute, '/prearrivals/signin');
    } else {
      RedirectHelper.redirectByUrl(this.ngZone, this.router, this.activatedRoute, '/signin');
    }
  }

  proceedIfValid(captchaResponseV2: string | null) {
    if (this.passwordRecoveryInProgress || !captchaResponseV2) return;
    this.proceedRecovery(captchaResponseV2);
  }

  private proceedRecovery(captchaResponseV2: string) {
    this.passwordRecoveryInProgress = true;
    const email = this.forgotPasswordForm.get('email').value?.trim();
    this.captchaV3Service.execute('forgotPassword').subscribe((captchaResponseV3: string) => {
      this.accountService.resetPasswordByEmail(email, captchaResponseV3, captchaResponseV2).subscribe(({ isSuccess }) => {
        this.passwordRecoveryInProgress = false;
        if (isSuccess) {
          this.captchaElem.reset();
          this.onLoginClick();
          SnackbarHelper.showTranslatableSnackBar(
            this.ngZone,
            this.snackBar,
            this.translocoService,
            marker('translations.resetPasswordInstructionSend'),
          );
        } else {
          SnackbarHelper.showTranslatableSnackBar(
            this.ngZone,
            this.snackBar,
            this.translocoService,
            'translations.resetPasswordInstructionSend',
          );
        }
        this.cdr.detectChanges();
      });
    });
  }
}
